import { IonList, IonPage } from "@ionic/react";
import { TabRouterPath } from "@src/app/routing/constant/tabRoute";
import { AppBarHeader, PageWithHeader } from "@src/appV2/lib";
import { useHistory } from "react-router-dom";

import { usePreferredWorkplaceCount } from "./api/usePreferredWorkplaceCount";
import { CardLoader } from "./CardLoader";
import { pluralizeFacility, pluralizeFacilityWithThe } from "./lang";
import { logViewDetailsClicked } from "./logs";
import {
  Button,
  Card,
  CardTitle,
  ContentCol,
  Description,
  Grid,
  Item,
  RatingScore,
  Row,
  ScoreCol,
} from "./RatingsPage.styles";
import { getScoreSeverity } from "./utils";
import { useAttendanceScoreProfile } from "../attendancePolicy/api/useAttendanceScoreProfile";
import { useDefinedAgent } from "../store/helperHooks";

export function RatingsPage() {
  const history = useHistory();
  const { userId = "" } = useDefinedAgent();

  const {
    isLoading: isAttendanceScoreLoading,
    data: attendanceScoreData,
    refetch: refetchAttendanceScore,
  } = useAttendanceScoreProfile(userId, {
    meta: { userErrorMessage: "Something went wrong while loading your attendance score." },
  });
  const {
    isLoading: isPreferredWorkplaceLoading,
    data: preferredWorkplaceData,
    refetch: refetchPreferredWorkplace,
  } = usePreferredWorkplaceCount(userId, {
    meta: {
      userErrorMessage: "Something went wrong while loading your priority access facilities.",
    },
  });

  const handleAttendanceScoreClicked = () => {
    logViewDetailsClicked(userId, "attendance score");
    history.push(TabRouterPath.RATINGS_ATTENDANCE_SCORE);
  };

  const handlePriorityAccessClicked = () => {
    logViewDetailsClicked(userId, "priority access");
    history.push(TabRouterPath.RATINGS_PRIORITY_ACCESS);
  };

  const numberOfPriorityAccesses = preferredWorkplaceData?.workplaceCountByReason
    ? preferredWorkplaceData.workplaceCountByReason.RATING +
      preferredWorkplaceData.workplaceCountByReason.INTERNAL_CRITERIA +
      preferredWorkplaceData.workplaceCountByReason.FAVORITE
    : undefined;

  return (
    <IonPage>
      <PageWithHeader
        appBarHeader={<AppBarHeader title="My Ratings" />}
        containerVariant="without-margin"
        containerMaxWidth="lg"
        onRefresh={() => Promise.all([refetchAttendanceScore(), refetchPreferredWorkplace()])}
      >
        {isPreferredWorkplaceLoading || isAttendanceScoreLoading ? (
          <CardLoader />
        ) : (
          <>
            {attendanceScoreData && (
              <Card>
                <Grid>
                  <Row>
                    <ContentCol>
                      <IonList lines="none">
                        <Item>
                          <CardTitle>My Attendance Score</CardTitle>
                        </Item>
                        <Item>
                          <Description>
                            Your current Attendance Score is {attendanceScoreData.score}. Learn more
                            about how Attendance Score works.
                          </Description>
                        </Item>
                        <Item>
                          <Button
                            fill="outline"
                            shape="round"
                            color={getScoreSeverity(attendanceScoreData.score)}
                            data-testid="view-attendance-score-button"
                            onClick={handleAttendanceScoreClicked}
                          >
                            See details
                          </Button>
                        </Item>
                      </IonList>
                    </ContentCol>
                    <ScoreCol>
                      <RatingScore
                        color={getScoreSeverity(attendanceScoreData.score)}
                        data-testid="attendance-score"
                        length={attendanceScoreData.score.toString().length}
                      >
                        <span>{attendanceScoreData.score}</span>
                      </RatingScore>
                    </ScoreCol>
                  </Row>
                </Grid>
              </Card>
            )}
            {numberOfPriorityAccesses !== undefined && (
              <Card>
                <Grid>
                  <Row>
                    <ContentCol>
                      <IonList lines="none">
                        <Item>
                          <CardTitle>My Priority Access</CardTitle>
                        </Item>
                        <Item>
                          <Description>
                            You have Priority Access at{" "}
                            {pluralizeFacility(numberOfPriorityAccesses)}.{" "}
                            {numberOfPriorityAccesses === 0 ? (
                              "Learn more about how Priority Access works."
                            ) : (
                              <>
                                {" "}
                                View {pluralizeFacilityWithThe(numberOfPriorityAccesses)} and learn
                                more about how Priority Access works.
                              </>
                            )}
                          </Description>
                        </Item>
                        <Item>
                          <Button
                            fill="outline"
                            shape="round"
                            color="shift-priority"
                            size="default"
                            data-testid="view-priority-access-button"
                            onClick={handlePriorityAccessClicked}
                          >
                            See details
                          </Button>
                        </Item>
                      </IonList>
                    </ContentCol>
                    <ScoreCol>
                      <RatingScore
                        color="shift-priority"
                        data-testid="priority-access-workplaces-account"
                        length={numberOfPriorityAccesses.toString().length}
                      >
                        <span>{numberOfPriorityAccesses}</span>
                      </RatingScore>
                    </ScoreCol>
                  </Row>
                </Grid>
              </Card>
            )}
          </>
        )}
      </PageWithHeader>
    </IonPage>
  );
}
